import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import type { IssueMessage } from './issue-message.js';

/**
 * Presentasjonskomponent for en meldingstråd på et anonymt avvik. Brukes på begge
 * sider — behandler (`viewer="handler"`) og melder (`viewer="reporter"`). Backend
 * setter author, så denne viser bare tråden + et skrivefelt og sender `send`-event
 * med `{ body }`. Stenges (`closed`) når avviket er ferdigbehandlet.
 */
@customElement('d-issue-message-thread')
export class DIssueMessageThread extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      font-family: var(--mainSans, system-ui, sans-serif);
    }
    .thread {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 16px;
    }
    .empty {
      font-size: 14px;
      color: hsla(0, 0%, 0%, 0.45);
      margin-bottom: 16px;
    }
    .msg {
      border: 1px solid var(--borderColor, #d6d3cc);
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 14px;
      line-height: 1.45;
      background: white;
    }
    .msg.own {
      background: hsl(205, 60%, 96%);
      border-color: hsl(205, 40%, 85%);
    }
    .meta {
      font-size: 12px;
      color: hsla(0, 0%, 0%, 0.5);
      margin-bottom: 4px;
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }
    textarea {
      width: 100%;
      box-sizing: border-box;
      min-height: 72px;
      padding: 10px;
      border: 1px solid var(--borderColor, #d6d3cc);
      border-radius: 6px;
      font: inherit;
      resize: vertical;
    }
    .actions {
      display: flex;
      gap: 8px;
      margin-top: 8px;
    }
    button {
      font: inherit;
      font-size: 14px;
      border-radius: 6px;
      padding: 8px 14px;
      cursor: pointer;
      border: 1px solid transparent;
      background: var(--linkColorGray, #2d2d2d);
      color: white;
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .closedNote {
      padding: 12px;
      border-radius: 6px;
      background: hsl(0, 0%, 95%);
      font-size: 14px;
      color: hsla(0, 0%, 0%, 0.7);
    }
  `;

  @property({ type: Array })
  messages: IssueMessage[] = [];

  /** Hvem som ser tråden — styrer «Deg» vs motpartens navn. */
  @property({ type: String })
  viewer: 'reporter' | 'handler' = 'handler';

  /** Avviket er ferdigbehandlet — skrivefeltet stenges. */
  @property({ type: Boolean })
  closed = false;

  @state()
  private draft = '';

  private label(message: IssueMessage): string {
    if (message.author === this.viewer) return t('issues.msgLabelYou');
    if (message.author === 'handler') return message.authorName ?? t('issues.msgLabelHandler');
    return t('issues.msgLabelReporter');
  }

  private formatTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' });
  }

  private send() {
    const body = this.draft.trim();
    if (body === '') return;
    this.dispatchEvent(new CustomEvent('send', { bubbles: true, composed: true, detail: { body } }));
    this.draft = '';
  }

  render() {
    return html`
      ${this.messages.length === 0
        ? html`<div class="empty">${t('issues.msgEmpty')}</div>`
        : html`<div class="thread">
            ${this.messages.map(
              (m) => html`
                <div class="msg ${m.author === this.viewer ? 'own' : ''}">
                  <div class="meta"><span>${this.label(m)}</span><span>${this.formatTime(m.createdAt)}</span></div>
                  <div>${m.body}</div>
                </div>
              `,
            )}
          </div>`}
      ${this.closed
        ? html`<div class="closedNote">${t('issues.msgClosedNote')}</div>`
        : html`
            <textarea
              .value=${this.draft}
              placeholder=${this.viewer === 'handler'
                ? t('issues.msgPlaceholderHandler')
                : t('issues.msgPlaceholderReporter')}
              @input=${(e: Event) => {
                this.draft = (e.target as HTMLTextAreaElement).value;
              }}
            ></textarea>
            <div class="actions">
              <button ?disabled=${this.draft.trim() === ''} @click=${() => this.send()}>${t('issues.msgSend')}</button>
            </div>
          `}
      ${nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issue-message-thread': DIssueMessageThread;
  }
}
